import type {
  DisplayDateFormatId,
  DisplayDateTimeFormatId,
  DisplayTimeFormatId,
} from '@/utils/displayFormatConstants';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

type DateParts = {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
  dayPeriod?: string;
};

function partsFromDate(date: Date, timeZone: string, locale: string, hour12: boolean): DateParts {
  const map = Object.fromEntries(
    new Intl.DateTimeFormat(locale, {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12,
    })
      .formatToParts(date)
      .map((p) => [p.type, p.value]),
  );
  return {
    year: map.year ?? '',
    month: map.month ?? '',
    day: map.day ?? '',
    hour: map.hour ?? '00',
    minute: map.minute ?? '00',
    second: map.second ?? '00',
    dayPeriod: map.dayPeriod,
  };
}

function monthShort(date: Date, timeZone: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { timeZone, month: 'short' }).format(date);
}

function monthShortFromIsoDate(isoDate: string, locale: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(Date.UTC(year, month - 1, day)));
}

function pad2(value: string | number): string {
  return String(value).padStart(2, '0');
}

function formatTimeFromParts(parts: DateParts, preset: DisplayTimeFormatId): string {
  if (preset === 'TIME_12') {
    const hour = Number(parts.hour);
    const minute = pad2(parts.minute);
    const period = (parts.dayPeriod ?? 'AM').toUpperCase();
    return `${hour}:${minute} ${period}`;
  }
  return `${pad2(parts.hour)}:${pad2(parts.minute)}:${pad2(parts.second)}`;
}

/** Parse API date/time strings as UTC instants. */
export function parseStoredUtcValue(value: string): Date {
  const trimmed = value.trim();
  if (!trimmed) return new Date(NaN);
  if (trimmed.endsWith('Z') || /[+-]\d{2}(:\d{2})?$/.test(trimmed)) {
    return new Date(trimmed);
  }
  const iso = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
  return new Date(`${iso}Z`);
}

export function formatDateWithPreset(
  value: string | null | undefined,
  preset: DisplayDateFormatId,
  timeZone: string,
  locale: string,
): string {
  if (!value) return '—';
  const trimmed = value.trim();
  if (DATE_ONLY_RE.test(trimmed)) {
    const [year, , day] = trimmed.split('-');
    switch (preset) {
      case 'DAY_MON_YEAR':
        return `${pad2(day)} ${monthShortFromIsoDate(trimmed, locale)} ${year}`;
      case 'MON_DAY_YEAR':
        return `${monthShortFromIsoDate(trimmed, locale)} ${pad2(day)}, ${year}`;
      default:
        return trimmed;
    }
  }
  const date = parseStoredUtcValue(trimmed);
  if (Number.isNaN(date.getTime())) return trimmed;
  const parts = partsFromDate(date, timeZone, locale, false);
  switch (preset) {
    case 'DAY_MON_YEAR':
      return `${pad2(parts.day)} ${monthShort(date, timeZone, locale)} ${parts.year}`;
    case 'MON_DAY_YEAR':
      return `${monthShort(date, timeZone, locale)} ${pad2(parts.day)}, ${parts.year}`;
    default:
      return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
  }
}

export function formatWallClockTime(value: string, preset: DisplayTimeFormatId): string {
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(value.trim());
  if (!match) return value;
  const hour = Number(match[1]);
  const minute = pad2(match[2]);
  const second = pad2(match[3] ?? '0');
  if (preset === 'TIME_12') {
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute} ${period}`;
  }
  return `${pad2(hour)}:${minute}:${second}`;
}

export function formatTimeWithPreset(
  value: string | Date | null | undefined,
  preset: DisplayTimeFormatId,
  timeZone: string,
  locale: string,
): string {
  if (!value) return '—';
  if (typeof value === 'string' && !value.includes('T') && /^\d{1,2}:\d{2}/.test(value.trim())) {
    return formatWallClockTime(value, preset);
  }
  const date = value instanceof Date ? value : parseStoredUtcValue(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  const parts = partsFromDate(date, timeZone, locale, preset === 'TIME_12');
  return formatTimeFromParts(parts, preset);
}

export function formatDateTimeWithPreset(
  value: string | null | undefined,
  dateTimePreset: DisplayDateTimeFormatId,
  _datePreset: DisplayDateFormatId,
  _timePreset: DisplayTimeFormatId,
  timeZone: string,
  locale: string,
): string {
  if (!value) return '—';
  const date = parseStoredUtcValue(value);
  if (Number.isNaN(date.getTime())) return value;

  if (dateTimePreset === 'ISO_DATETIME') {
    const parts = partsFromDate(date, timeZone, locale, false);
    return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)} ${pad2(parts.hour)}:${pad2(parts.minute)}:${pad2(parts.second)}`;
  }

  if (dateTimePreset === 'ISO_DATETIME_TIME_12') {
    const parts = partsFromDate(date, timeZone, locale, true);
    return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)} ${formatTimeFromParts(parts, 'TIME_12')}`;
  }

  const resolvedTimePreset: DisplayTimeFormatId =
    dateTimePreset === 'DAY_MON_YEAR_TIME_12' ? 'TIME_12' : 'TIME_24';
  const dateLabel = formatDateWithPreset(value, 'DAY_MON_YEAR', timeZone, locale);
  const timeLabel = formatTimeWithPreset(date, resolvedTimePreset, timeZone, locale);
  return `${dateLabel}, ${timeLabel}`;
}

export function previewDisplayFormats(
  sampleUtcIso: string,
  dateFormat: DisplayDateFormatId,
  timeFormat: DisplayTimeFormatId,
  dateTimeFormat: DisplayDateTimeFormatId,
  timeZone: string,
  locale: string,
): { date: string; time: string; dateTime: string } {
  return {
    date: formatDateWithPreset(sampleUtcIso, dateFormat, timeZone, locale),
    time: formatTimeWithPreset(sampleUtcIso, timeFormat, timeZone, locale),
    dateTime: formatDateTimeWithPreset(
      sampleUtcIso,
      dateTimeFormat,
      dateFormat,
      timeFormat,
      timeZone,
      locale,
    ),
  };
}
